import {
  FaEquals,
  FaNotEqual,
  FaGreaterThanEqual,
  FaGreaterThan,
  FaLessThanEqual,
  FaLessThan,
} from 'react-icons/fa';
import React from 'react';
import { Box, Text, Flex } from 'rebass/styled-components';
import Papa from 'papaparse';
import { ConfigRuleActionType } from '@liveperson/lpcontextschemalib';
import { PolicyConditionOperator } from '../../enums';
import api from '../../api';
import { attributeRegEx, channelRegEx, listRegEx } from '../../util';

enum ConfigEditorTypeExtra {
  FAAS = 'faas',
  FALLBACK_SKILL = 'fallback-skill',
  STRING = 'string',
  FALLBACK_ATTRIBUTE = 'fallback-attribute',
}

export type ConfigEditorType = ConfigRuleActionType | ConfigEditorTypeExtra;

const parseValue = (valueType: string, value: any) => {
  switch (valueType) {
    case 'attribute':
      return value.trim().substr(2).slice(0, -2);
    case 'channel':
      return value.replace(/^%%/, '').replace(/%%$/, '');
    case 'list':
      if (Array.isArray(value)) {
        return value.map((item) => `"${item}"`).join(', ');
      }
      return value.trim().substr(1).slice(0, -1);
    case 'object':
      return '';
    case 'number':
      return Number.isNaN(value) ? '' : value;
    default:
      return value;
  }
};

const parseTitle = (value: any, actionType?: ConfigEditorType) => {
  if (typeof value === 'boolean') {
    return value.toString();
  }
  if (typeof value === 'string') {
    return value;
  }
  if (typeof value === 'object') {
    if (value.type) {
      return value.type;
    }
    if (actionType === ConfigRuleActionType.TRANSFER_TO_AGENT) {
      return `${value.fullName} (${value.id})`;
    }
    if (
      value.icon === ConfigRuleActionType.TRANSFER_TO_AGENT
      || actionType === ConfigRuleActionType.TRANSFER_TO_SKILL
      || actionType === ConfigEditorTypeExtra.FAAS
      || actionType === ConfigEditorTypeExtra.FALLBACK_SKILL
    ) {
      return `${value.name} (${value.id})`;
    }
    if (actionType === ConfigEditorTypeExtra.STRING) {
      return `${value.name} (${value.domain})`;
    }
  }
  return 'undefined';
};

const parseNumber = (value: string) => {
  // If the user types a minus character, we set the value to be -1, so that it is a number
  if (value === '-') {
    return -1;
  }
  // Otherwise, parse the string into a number
  return parseInt(value, 10);
};

const checkValueType = (value: any) => {
  const channels = api.getChannels();
  const channelDictionary = new Set(channels);

  // 1. check attribute
  if (typeof value === 'string' && value.match(attributeRegEx)) {
    return 'attribute';
  }
  // 2. check list
  if (
    (Array.isArray(value) && value.every((item) => typeof item === 'string'))
    || (typeof value === 'string' && value.match(listRegEx))
  ) {
    return 'list';
  }
  // 3. check channel
  if (typeof value === 'string' && (value.match(channelRegEx) || channelDictionary.has(value.replace(/^%%/, '').replace(/%%$/, '')))) {
    return 'channel';
  }
  // 4. check string
  if (typeof value === 'string') {
    return 'string';
  }
  // 5. check bool
  if (typeof value === 'boolean') {
    return 'boolean';
  }
  // 6. check number
  if (typeof value === 'number') {
    return 'number';
  }
  // 7. any other type is json
  return 'json';
};

const parseToArray = (value: any) => {
  if (!Array.isArray(value)) {
    const cleanPayload = value.substr(1).slice(0, -1);
    const [parsedContext] = Papa.parse(cleanPayload, {
      // removing extra whitespace that papaparse adds
      transform: (item: string) => {
        if (item.match(/^ +".+"$/g)) {
          return item.trim().substr(1).slice(0, -1);
        }
        return item.trim();
      },
    }).data;
    return parsedContext && (parsedContext as Array<any>).map((item: any) => (typeof item === 'string' ? item.trim() : item));
  }
  return value;
};

const getRowTypeIcon = (type: string, isCcsV2 = false): React.ReactNode => {
  switch (type) {
    case 'EQUAL':
      return <FaEquals size={12} />;
    case 'NOT_EQUAL':
      return <FaNotEqual size={12} />;
    case 'LESS_THAN':
      return <FaLessThan size={12} />;
    case 'LESS_THAN_OR_EQUAL':
      return <FaLessThanEqual size={12} />;
    case 'GREATER_THAN':
      return <FaGreaterThan size={12} />;
    case 'GREATER_THAN_OR_EQUAL':
      return <FaGreaterThanEqual size={12} />;
    case 'IS_IN':
      return isCcsV2 ? 'IN' : 'Is in';
    case 'CONTAINS':
      return 'Contains';
    case 'IN':
      return 'IN';
    default:
      return <Text>...</Text>;
  }
};

const parseDropdownValue = (value: any, actionType?: string, isCcsV2 = false) => {
  if (typeof value === 'boolean') {
    return value.toString();
  }
  if (typeof value === 'string') {
    if (value === 'TRANSFER_TO_AGENT') {
      return 'Transfer to agent';
    }
    if (value === 'TRANSFER_TO_SKILL') {
      return 'Transfer to skill';
    }
    if (value === 'SEND_MESSAGE') {
      return 'Send message';
    }
    return value;
  }
  if (typeof value === 'object') {
    if (value.type) {
      return getRowTypeIcon(value.type, isCcsV2);
    }
    if (actionType === 'TRANSFER_TO_AGENT') {
      return (
        <Box alignItems="center" justifyContent="space-between" flex={1}>
          <Text mr={1}>{value.fullName}</Text>
          <Text color="midGrey">{`id: ${value.id}`}</Text>
        </Box>
      );
    }
    if (actionType === 'TRANSFER_TO_SKILL' || actionType === 'faas' || actionType === 'fallback-skill') {
      return (
        <Box alignItems="center" justifyContent="space-between" flex={1}>
          <Text mr={1}>{value.name}</Text>
          <Text color="midGrey">{`id: ${value.id}`}</Text>
        </Box>
      );
    }
    if (actionType === 'string') {
      return (
        <Box alignItems="center" justifyContent="space-between" flex={1}>
          <Text mr={1}>{value.name}</Text>
          <Text color="midGrey">{`domain: ${value.domain}`}</Text>
        </Box>
      );
    }
  }
  return 'undefined';
};

const parseName = (
  value: any, actionType?: ConfigEditorType, placeholder?: string,
  hasItems = false, isEditing = false,
) => {
  if (typeof value === 'boolean') {
    return value.toString();
  }
  if (typeof value === 'string') {
    if (value.length === 0) {
      return '';
    }
    if (checkValueType(value) === 'attribute') {
      return value.substr(2).slice(0, -2);
    }
    if (checkValueType(value) === 'channel') {
      return value.replace(/^%%/, '').replace(/%%$/, '');
    }
    return value;
  }
  if (typeof value === 'object') {
    if (value.type) {
      return <Flex alignItems="center" height={16}>{getRowTypeIcon(value.type)}</Flex>;
    }
    if (actionType === 'TRANSFER_TO_AGENT') {
      return value.fullName;
    }
    if (actionType === 'TRANSFER_TO_SKILL' || actionType === 'string' || actionType === 'fallback-skill') {
      return value.name;
    }
  }
  if (typeof value === 'number' && !isEditing) {
    return `${value}`;
  }
  return placeholder;
};

export {
  parseValue,
  getRowTypeIcon,
  parseDropdownValue,
  parseToArray,
  parseNumber, checkValueType, parseTitle,
  parseName,
};
